import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, isNumberAndNotNan, percentageSignFilter, yAxisChangeFilter } from "app/filters/ngFilters";
import { PREVIOUS_PERIOD, PREVIOUS_YEAR } from "app/services/periodOverPeriodTypes";

import { EChangeFilterValues } from "app/components/filters-bar/change-filter/ChangeFilter";
import {
    channelsToShow,
    totalChangeRow,
    MINUS_SIGN,
    PLUS_SIGN,
    POP_DEFAULT_DURATION,
    YOY_DEFAULT_DURATION,
} from "../consts";
import type { CellFormat, ChannelMetrics, TrafficTotals, ChangeValue } from "../types";

const { black } = colorsPalettes;

export const getDescriptionText = (isPoP: boolean, key: string) => (
    <Text
        fontVariant="captionTitle"
        color={black[0]}
    >
        {translate(key)} ({translate(isPoP ? "common.pop.change" : "common.yoy.change")})
    </Text>
);

export const computeChange = (curr: number, prev: number) => ({
    absolute: !prev && curr ? EChangeFilterValues.NewClicks : curr - prev,
    percentage: !prev ? (curr ? EChangeFilterValues.NewClicks : 0) : (curr - prev) / prev,
});

export const processChannel = (curr?: TrafficTotals, prev?: TrafficTotals): ChannelMetrics => {
    const allSites = new Set([...Object.keys(curr ?? {}), ...Object.keys(prev ?? {})]);
    return Array.from(allSites).reduce<ChannelMetrics>((acc, site) => {
        const currVal = curr?.[site] ?? 0;
        const prevVal = prev?.[site] ?? 0;
        acc[site] = { value: currVal, change: computeChange(currVal, prevVal) };
        return acc;
    }, {});
};

export const formatRow = (metrics: ChannelMetrics, format: CellFormat) =>
    Object.fromEntries(Object.entries(metrics).map(([site, { change }]) => [site, change[format]]));

export const formatChangeWithSign = (value: ChangeValue): string => {
    if (value === EChangeFilterValues.NewClicks) {
        return value;
    }
    if (value === null || Math.abs(value) < 0.0001) {
        return NO_DATA_SIGN;
    }

    const abs = Math.abs(value);
    const formatted = yAxisChangeFilter()(abs);
    const sign = value > 0 ? PLUS_SIGN : MINUS_SIGN;

    return abs >= 50 ? formatted : `${sign}${formatted}`;
};

export const customAbbrNumberSupportNegativeFilter =
    () =>
    (value: ChangeValue, noZeros?: boolean): string => {
        if (value === EChangeFilterValues.NewClicks) {
            return value;
        }
        if (value === null || value === 0) {
            return NO_DATA_SIGN;
        }

        const formatted = abbrNumberFilter()(Math.abs(value), noZeros);
        return value < 0 ? `${MINUS_SIGN}${formatted}` : formatted;
    };

export function percentageSignOrZeroWrapper() {
    const baseFilter = percentageSignFilter();
    return (val, decimal = 2) => {
        if (!isNumberAndNotNan(val)) {
            return "0%";
        }
        return baseFilter(val, decimal);
    };
}
export const getComparisonParams = (
    labelKey: string,
    compareTypes: { id: string; isDisabled: boolean }[],
    comparedDuration?: string,
) => {
    const isPoP = labelKey === "common.pop";
    const periodId = isPoP ? PREVIOUS_PERIOD : PREVIOUS_YEAR;
    const isDisabled = compareTypes.find(({ id }) => id === periodId)?.isDisabled;
    const defaultDuration = isPoP ? POP_DEFAULT_DURATION : YOY_DEFAULT_DURATION;

    return {
        isPoP,
        resolvedDuration: comparedDuration || (isDisabled ? "" : defaultDuration),
        defaultDuration,
    };
};

export function buildChannelMetrics(
    current: Record<string, { Total: Record<string, number> }>,
    previous: Record<string, { Total: Record<string, number> }>,
) {
    const channelMetricsMap = new Map<string, ChannelMetrics>();
    const allDomains = new Set<string>();

    Object.keys(current).forEach((channelKey) => {
        const metrics = processChannel(current[channelKey]?.Total, previous[channelKey]?.Total);
        channelMetricsMap.set(channelKey, metrics);
        Object.keys(metrics).forEach((domain) => allDomains.add(domain));
    });

    const totalMetrics: ChannelMetrics = {};
    allDomains.forEach((site) => {
        const value = Array.from(channelMetricsMap.values()).reduce(
            (sum, channel) => sum + (channel[site]?.value ?? 0),
            0,
        );
        totalMetrics[site] = { value, change: { absolute: null, percentage: null } };
    });

    return { channelMetricsMap, totalMetrics };
}

export function computeTotalChanges(
    totalMetrics: ChannelMetrics,
    previousTraffic: Record<string, { Total: Record<string, number> }>,
): ChannelMetrics {
    return Object.fromEntries(
        Object.entries(totalMetrics).map(([site, { value }]) => {
            const prev = Object.values(previousTraffic).reduce((sum, { Total }) => sum + (Total?.[site] ?? 0), 0);
            return [site, { value, change: computeChange(value, prev) }];
        }),
    );
}

export const buildRowsFromMetrics = (
    total: ChannelMetrics,
    metricsMap: Map<string, ChannelMetrics>,
    format: CellFormat,
    allDomains: string[],
): Array<Record<string, unknown>> => {
    const buildRow = (metrics: ChannelMetrics) => {
        return allDomains.reduce<Record<string, unknown>>((acc, domain) => {
            const value = metrics?.[domain]?.change?.[format] ?? null;
            acc[domain] = value;
            return acc;
        }, {});
    };

    return [
        { ...totalChangeRow, ...buildRow(total) },
        ...channelsToShow
            .filter(({ key }) => metricsMap.has(key))
            .map(({ key, label }) => ({
                channel: label,
                ...buildRow(metricsMap.get(key)),
            })),
    ];
};
